





























































import Vue from 'vue';
import { Prop, Component, Watch } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';
import { Route } from 'vue-router';
import { postData } from 'frontend/utils/ajax';
import { stringifyQueryParams } from 'frontend/utils/urls';
import { isServerEnv } from 'frontend/utils/environment';

import {
  DailyChallenge,
  DcProblem,
  DcProblemLoggedOut,
  DcMainBody,
} from 'frontend/types/daily-challenges';
import BriocheImageTag from 'frontend/components/BriocheImageTag.vue';
import DailyChallengeFeaturedCourse from 'frontend/views/dailyproblems/components/DcFeaturedCourse.vue';
import { MobileGetter, User } from 'frontend/types';
import { initDcVisualization } from 'frontend/views/dailyproblems/utils/dcVisualization';
import {
  InteractiveSolvable,
  SolvableConfig,
  InteractiveConfig,
} from 'visualizations/shared/InteractiveModule';
import XPBadge from 'frontend/components/XPBadge.vue';
import { safeWindow } from 'frontend/utils/window';

@Component({
  components: {
    BriocheImageTag,
    DailyChallengeFeaturedCourse,
    XPBadge,
  },
  computed: {
    ...mapState('globals', ['user']),
    ...mapState('dailyChallenges', ['problem']),
    ...mapGetters('pageHeader', ['isMobile']),
  },
})
export default class DailyChallengeBody extends Vue {
  @Prop(Object) private dailyChallenge!: DailyChallenge;
  @Prop(Object) private mainbody!: DcMainBody;
  @Prop(Boolean) private isLocked!: boolean;
  @Prop(Boolean) private isCompleted!: boolean;

  private user!: User;
  private interactive?: InteractiveSolvable;
  private problem!: DcProblem | DcProblemLoggedOut;

  // solvable
  private caption = '';
  private isExpectingSolvedResponse: boolean = false;
  private isSubmitEnabled = false;
  private isResetEnabled = false;
  private isSolved?: boolean = this.dailyChallenge.complete;

  private renderedVisualizations: string[] = [];

  private expanded: boolean = false;
  private isMobile!: MobileGetter;

  private get isSeries(): boolean {
    return Number(this.dailyChallenge.series?.total) > 1;
  }

  private get alwaysFullVisible(): boolean {
    return this.user.email === 'sam+content-exporter@blackhole.brilliant.org';
  }

  private keepReading(): void {
    this.expanded = true;
    setTimeout(() => {
      safeWindow()?.dispatchEvent(new Event('resize'));
    }, 200);
  }

  private get showFeaturedCourse() {
    return this.isMobile(this.$mq);
  }

  private get processedMainbodyHead() {
    return this.mainbody.head.replace(/<img/g, '<img class="b-img-responsive"');
  }

  private get processedMainbodyTail() {
    return this.mainbody.tail.replace(/<img/g, '<img class="b-img-responsive"');
  }

  private setCaption(caption: string) {
    this.caption = caption;
  }

  private setSubmitButtonEnabled(enabled: boolean) {
    this.isSubmitEnabled = enabled;
    this.externalEvent('submit-enabled', {
      enabled: this.isSubmitEnabled,
    });
  }

  private externalEvent(name: string, data = {}) {
    this.$emit('external', {
      name: `${name}.${this.problem.solvable_id}`,
      ...data,
    });
  }

  private setResetButtonEnabled(enabled: boolean) {
    this.isResetEnabled = enabled;
  }

  private get isLoggedIn(): boolean {
    return this.user.is_active;
  }

  private handleSaveStateRequest(state: { [key: string]: any }, solved?: boolean) {
    if (this.dailyChallenge.complete || !this.interactive) {
      return;
    }

    if (!this.isLoggedIn) {
      return;
    }

    if (this.interactive.solvableType === 'submittable') {
      if (!this.isLoggedIn) {
        return;
      }

      if (!this.loggedInProblem.target_visualization) {
        return;
      }
      if (this.isExpectingSolvedResponse && solved !== undefined) {
        this.isSolved = solved;
        this.logAttemptToServer(state, solved);
        this.isExpectingSolvedResponse = false;
      } else {
        this.saveStateToServer(state);
      }
    } else if (this.interactive.solvableType === 'auto-check') {
      if (solved === true) {
        if (this.isLoggedIn) {
          this.isSolved = true;
          this.logAttemptToServer(state, solved);
        } else {
          this.externalEvent('show-signup-modal', { solvableType: this.interactive.solvableType });
        }
      } else {
        if (this.isLoggedIn) {
          this.saveStateToServer(state);
        } else {
          return;
        }
      }
    }
    return true;
  }

  private get loggedInProblem(): DcProblem {
    return this.problem as DcProblem;
  }

  private saveStateToServer(state: { [key: string]: any }) {
    if (!this.isLoggedIn) {
      // We can't save state for logged out users.
      return;
    }
    const ATTEMPT_STATE_ENDPOINT = '/api/v1/problems/attempts/update-state/';
    this.externalEvent('export-state', { state });
    if (this.loggedInProblem.attempt_id > 0) {
      postData(
        ATTEMPT_STATE_ENDPOINT,
        stringifyQueryParams({
          attempt_id: this.loggedInProblem.attempt_id,
          state: JSON.stringify(state),
        }),
      );
    }
  }

  private logAttemptToServer(state: { [key: string]: any }, solved: boolean) {
    this.saveStateToServer(state);
    this.externalEvent('check-answer', { isSolved: solved });
  }

  @Watch('mainbody.visualizations')
  private async checkVisualizations(): Promise<void> {
    if (isServerEnv) {
      return;
    }
    for (const config of this.mainbody.visualizations) {
      const key = config.slug;
      if (key in this.renderedVisualizations) {
        continue;
      }
      const solvableType = config.authoredData.solvableType;
      const interactiveConfig = {
        slug: config.slug,
        visualizationKey: config.slug,
        authoredData: config.authoredData,
        generatedData: config.generatedData,
        location: 'v2-quiz',
      } as InteractiveConfig;

      if (solvableType && 'submittable' === solvableType) {
        const solvableConfig: SolvableConfig = {
          hasBeenSolved: this.dailyChallenge.complete === true,
          callbacks: {
            setCaption: caption => this.setCaption(caption),
            setSubmitButtonEnabled: enabled => this.setSubmitButtonEnabled(enabled),
            setResetButtonEnabled: enabled => this.setResetButtonEnabled(enabled),
            saveState: (state, solved) => this.handleSaveStateRequest(state, solved),
          },
        };
        this.interactive = await initDcVisualization(
          config.type,
          interactiveConfig,
          solvableConfig,
        );
      } else {
        await initDcVisualization(config.type, interactiveConfig);
      }
      this.renderedVisualizations.push(key);
    }
  }

  private async mounted() {
    this.$nextTick(async () => {
      await this.checkVisualizations();
    });
  }

  @Watch('$route', { immediate: true, deep: true })
  private async onDailyChallengeView(newVal: Route) {
    if (this.mainbody.visualizations.length > 0) {
      this.expanded = true;
      // some 3d visualizations do not render properly if they are not visible during initialization
      await this.checkVisualizations();
      this.$nextTick(() => {
        this.expanded = this.alwaysFullVisible || this.isMobile(this.$mq);
      });
    } else {
      this.expanded = this.alwaysFullVisible || this.isMobile(this.$mq);
    }
    this.loadVideo();
  }

  private async loadVideo() {
    if (process.env.VUE_ENV === 'server') {
      return;
    }
    const videoContainers = document.getElementsByClassName('video-container');
    if (videoContainers.length > 0) {
      for (const videoContainer of videoContainers) {
        const video = videoContainer.querySelector('video');
        const assets = videoContainer.getAttribute('data-assets');
        const sources = JSON.parse(assets as string);
        if (video && sources.length) {
          for (const source of sources) {
            const sourceElement = document.createElement('source');
            sourceElement.src = source.url;
            video.appendChild(sourceElement);
          }
        }
      }
    }
  }
}
